/**
 * The one place that performs Babylon.js side-effect registration (convention from jreb-sailing).
 *
 * HOW TO IMPORT BABYLON (every module):
 *   import { Scene, Mesh, Vector3 } from '@babylonjs/core/pure';
 *   - Named imports from the pure barrel only; never from '@babylonjs/core' root.
 *   - Builders/materials/lights used by this game are registered here (idempotent).
 *
 * Registered for this game (Babylon 9.28):
 *   - thin instances + regular instances (crowd, bullets)
 *   - PBRMaterial + StandardMaterial (realism pipeline / unlit emissive FX)
 *   - mesh builders: box, sphere, ground, plane, cylinder, capsule, torus
 *   - lights: directional (IBL is the sky fill since plan 08 G1)
 *   - post: DefaultRenderingPipeline (bloom), colour curves
 *   - textures: DynamicTexture (gate numbers), cube/HDR (IBL environment)
 *   - engine queries (perf overlay GPU/draw-call counters)
 */
import {
  RegisterAbstractEngineCubeTexture,
  RegisterAbstractEngineQuery,
  RegisterAbstractEngineTimeQuery,
  RegisterCapsuleBuilder,
  RegisterCylinderBuilder,
  RegisterDirectionalLight,
  RegisterEnginesExtensionsEngineCubeTexture,
  RegisterEnginesExtensionsEngineDynamicTexture,
  RegisterEnginesExtensionsEngineQuery,
  RegisterEnginesExtensionsEngineRawTexture,
  RegisterGroundBuilder,
  RegisterColorCurves,
  RegisterDefaultRenderingPipeline,
  RegisterImageProcessingConfiguration,
  PostProcessRenderPipelineManager,
  RegisterPostProcessRenderPipelineManagerSceneComponent,
  RegisterInstancedMesh,
  RegisterPBRMaterial,
  RegisterPlaneBuilder,
  RegisterSphereBuilder,
  RegisterStandardMaterial,
  RegisterThinEngineScissor,
  RegisterThinInstanceMesh,
  RegisterTorusBuilder,
  RegisterStandardEngineExtensions,
  RegisterBoxBuilder,
} from '@babylonjs/core/pure';
import '@babylonjs/core/Meshes/thinInstanceMesh.types';

/** Scene-level features shared by both backends. Idempotent. */
export function registerSceneFeatures(): void {
  RegisterThinInstanceMesh();
  RegisterInstancedMesh();
  RegisterPBRMaterial();
  RegisterStandardMaterial();
  RegisterBoxBuilder();
  RegisterSphereBuilder();
  RegisterGroundBuilder();
  RegisterPlaneBuilder();
  RegisterCylinderBuilder();
  RegisterCapsuleBuilder();
  RegisterTorusBuilder();
  RegisterDirectionalLight();
  // Plan 08 G1: bloom pipeline + colour grading. The manager registration must be passed the class
  // or the first pipeline construction throws on an undefined constructor.
  RegisterImageProcessingConfiguration();
  RegisterColorCurves();
  RegisterDefaultRenderingPipeline();
  RegisterPostProcessRenderPipelineManagerSceneComponent(PostProcessRenderPipelineManager);
}

/** Engine extensions for the WebGL2 `Engine`. Call before `new Engine(…)`. Idempotent. */
export function registerWebGL2Engine(): void {
  RegisterStandardEngineExtensions();
  RegisterThinEngineScissor();
  RegisterEnginesExtensionsEngineRawTexture();
  RegisterEnginesExtensionsEngineDynamicTexture();
  RegisterAbstractEngineCubeTexture();
  RegisterEnginesExtensionsEngineCubeTexture();
  RegisterAbstractEngineQuery();
  RegisterAbstractEngineTimeQuery();
  RegisterEnginesExtensionsEngineQuery();
}
